import React, { useState } from "react";

import { SettingsConfirmModal } from "../../../../components/modal/SettingsConfirmModal";
import { TrashSvg } from "../../../../components/svg/TrashSvg";
import { getNoteDeleteTitle } from "./Note.helpers";

/**
 * Asks before a note is deleted (Roadmap 1806). `requestDelete(note)` opens
 * the confirmation; the note is deleted only after "Delete" is clicked.
 * Render `confirmModal` once in the component.
 */
export const useNoteDeleteConfirm = <T,>(
  onDelete: (note: T) => unknown,
  getTitle: (note: T) => string | null | undefined,
) => {
  const [pending, setPending] = useState<T | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const confirm = async () => {
    if (pending === null || isDeleting) return;
    setIsDeleting(true);
    try {
      await onDelete(pending);
    } catch {
      // The caller shows its own error message.
    } finally {
      setIsDeleting(false);
      setPending(null);
    }
  };

  const confirmModal =
    pending !== null ? (
      <SettingsConfirmModal
        onClose={() => {
          if (!isDeleting) setPending(null);
        }}
        onConfirm={confirm}
        isLoading={isDeleting}
        icon={<TrashSvg />}
        title={getNoteDeleteTitle(getTitle(pending))}
        description={"Do you want to permanently delete this note?"}
        confirmText={"Delete"}
        variant={"danger"}
      />
    ) : null;

  return { requestDelete: (note: T) => setPending(note), confirmModal };
};
